import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import SmallButton from "@ats/src/components/shared/SmallButton";

export interface AiCreditOneOff {
  credits: number;
  priceDollars: number;
  lookupKey: string;
}

interface AiCreditOneOffCardProps {
  purchase: AiCreditOneOff;
  isLoading?: boolean;
  onBuy: (purchase: AiCreditOneOff) => void;
}

const AiCreditOneOffCard: React.FC<AiCreditOneOffCardProps> = ({ purchase, isLoading = false, onBuy }) => {
  return (
    <Styled.Wrapper data-testid={`ai-one-off-card-${purchase.lookupKey}`}>
      <Styled.Accent />
      <Styled.Inner>
        <div>
          <Styled.Price>
            <h4>${purchase.priceDollars}</h4>
          </Styled.Price>
          <Styled.CreditAmount>{purchase.credits.toLocaleString()} credits</Styled.CreditAmount>
          <Styled.CreditLabel>one-time</Styled.CreditLabel>
        </div>
        <Styled.Buttons>
          <Styled.Button onClick={() => onBuy(purchase)} loading={isLoading} styleType="primary">
            Buy credits
          </Styled.Button>
        </Styled.Buttons>
      </Styled.Inner>
    </Styled.Wrapper>
  );
};

export default AiCreditOneOffCard;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Wrapper = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditOneOffCard;
    ${[t.rounded.md]}
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
    color: ${t.dark ? t.color.gray[300] : t.color.black};
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 196px;
    width: 100%;
    flex-grow: 1;
    min-width: 200px;
    max-width: 224px;
  `;
});

Styled.Accent = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditOneOffCard_Accent;
    height: 4px;
    background: ${t.dark ? "transparent" : "linear-gradient(84.47deg, #fbd7ff 0%, #ffe4cc 100%, #ffdec1 100%)"};
  `;
});

Styled.Inner = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditOneOffCard_Inner;
    ${[t.p(5)]}
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  `;
});

Styled.Price = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditOneOffCard_Price;
    color: ${t.dark ? t.color.gray[100] : t.color.black};
    h4 {
      ${[t.text.bold]}
      font-size: 2rem;
      line-height: 1;
      font-variant-numeric: tabular-nums;
    }
  `;
});

Styled.CreditAmount = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditOneOffCard_CreditAmount;
    ${[t.text.lg, t.text.semibold, t.mt(5)]}
    color: ${t.dark ? t.color.gray[100] : t.color.black};
    font-variant-numeric: tabular-nums;
  `;
});

Styled.CreditLabel = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditOneOffCard_CreditLabel;
    ${[t.text.xs, t.mt("px")]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.Buttons = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditOneOffCard_Buttons;
    display: flex;
    ${[t.mt(5)]}
  `;
});

Styled.Button = styled(SmallButton)(() => css`
  label: AiCreditOneOffCard_Button;
  min-width: 82px;
`);
